<script>
  import isEqual from "lodash/isEqual";
  import { draggable, clickOutside } from "svelte-utilities";

  import ReportSection from "./ReportSection.svelte";
  import copyToClipboard from "#src/extensions/copy-to-clipboard.js";
  import { discontiguousKey, modifierKey } from "#src/extensions/event-keys.js";

  export let group;
  export let data;
  export let report = null;
  export let columns;
  export let attachments;
  export let sent = false;
  export let isReviewer = false;
  export let center = true;
  export let paddingTop = null;

  let table;
  let selection = [];
  let currentCell = null;
  let clickingTable = false;
  let left = false;

  $: pt = paddingTop ? `padding-top: ${paddingTop};` : "";
  $: du = $group.data.settings.display_unit;
  $: group_by = report?.group_by === "opening" ? "collection_id" : "type_id";
  $: rowIndices = getRowIndices(data.sections);
  $: selected = updateSelected(selection);
  $: linkPrefix = getLinkPrefix(sent, isReviewer, $group);
  $: areaFormatted =
    du === "millimeters"
      ? `${(data.totals.block_area * 0.092903).toFixed(2)} m²`
      : `${data.totals.block_area.toFixed(2)} sf`;

  function getLinkPrefix(sent, isReviewer, $group) {
    if (isReviewer) {
      return `/jobs/${$group?.job_id}`;
    }
    return sent ? `/versions/${$group?.id}` : `/jobs/${$group?.job_id}/current`;
  }

  function updateSelected(selection) {
    const sel = {};
    selection.forEach((s) => {
      for (let row = s[0]; row <= s[2]; row++) {
        for (let col = s[1]; col <= s[3]; col++) {
          sel[`${row}.${col}`] = true;
        }
      }
    });

    return sel;
  }

  function getRowIndices(sections) {
    const indices = [];

    sections.forEach((section, g) => {
      section.rows.forEach((row, i) => {
        indices.push([g, i]);
      });
    });

    return indices;
  }

  function getEventTd(e) {
    try {
      const td = e.target.closest("td");
      if (!td) return null;
      if (!table.contains(td)) return null;

      const row = parseInt(td.getAttribute("data-row"));
      const col = parseInt(td.getAttribute("data-column"));
      const typeIndex = parseInt(td.getAttribute("data-type-index"));
      const typeRow = parseInt(td.getAttribute("data-type-row"));

      if (isNaN(row) || isNaN(col)) return null;

      return {
        el: td,
        row,
        col,
        typeIndex,
        typeRow,
      };
    } catch {
      return null;
    }
  }

  function select(td) {
    currentCell = { row: td.row, col: td.col };
    selection = [[td.row, td.col, td.row, td.col]];
  }

  function dragSelect(td) {
    if (currentCell) {
      const minR = Math.min(currentCell.row, td.row);
      const minC = Math.min(currentCell.col, td.col);
      const maxR = Math.max(currentCell.row, td.row);
      const maxC = Math.max(currentCell.col, td.col);
      selection[selection.length - 1] = [minR, minC, maxR, maxC];
    } else {
      select(td);
    }
  }

  function metaSelect(td) {
    currentCell = { row: td.row, col: td.col };
    const n = [td.row, td.col, td.row, td.col];

    if (selection.some((s) => isEqual(s, n))) {
      const index = selection.findIndex((s) => isEqual(s, n));
      selection.splice(index, 1);
    }

    selection.push(n);
  }

  function getEventTh(e) {
    try {
      const th = e.target.closest("th");
      if (!th) return null;
      if (!table.contains(th)) return null;

      const col = parseInt(th.getAttribute("data-column"));
      const row = parseInt(th.getAttribute("data-row"));
      const typeIndex = parseInt(th.getAttribute("data-type-index"));
      const typeRow = parseInt(th.getAttribute("data-type-row"));

      if (isNaN(col) && isNaN(row)) return null;

      // Row header
      if (isNaN(col)) {
        return {
          el: th,
          row,
          typeIndex,
          typeRow,
        };
      }

      // Column header
      return {
        el: th,
        col,
        typeIndex,
      };
    } catch {
      return null;
    }
  }

  function dragstart(e) {
    clickingTable = true;
    table.focus({ preventScroll: true });
    const td = getEventTd(e.detail.event);
    if (!td) return;

    if (e.detail.event.shiftKey) {
      dragSelect(td);
    } else if (discontiguousKey(e.detail.event)) {
      metaSelect(td);
    } else {
      select(td);
    }
  }

  function drag(e) {
    const td = getEventTd(e.detail.event);
    if (!td) return;

    dragSelect(td);
  }

  function dragend(e) {
    clickingTable = false;
    const td = getEventTd(e.detail.event);
    if (!td) return;

    dragSelect(td);
  }

  function deselect() {
    currentCell = null;
    selection = [];
  }

  function copy(e) {
    // Ignoring all but the last selection
    // TODO: support multiple selections
    const sel = selection[selection.length - 1];
    if (!sel) return;

    const result = [];

    for (let r = sel[0]; r <= sel[2]; r++) {
      const row = [];
      for (let c = sel[1]; c <= sel[3]; c++) {
        const [g, i] = rowIndices[r];
        const item = data.sections[g].rows[i];
        const col = columns[c];
        const value = item[col.prop];

        row.push(value);
      }
      result.push(row);
    }

    const dest = result.map((row) => row.join("\t")).join("\n");
    copyToClipboard(dest);
  }

  function keydown(e) {
    if (e.key === "c" && modifierKey(e)) {
      copy(e);
    }
  }

  function mouseleave(e) {
    if (clickingTable) {
      left = true;
    }
  }

  function mouseenter(e) {
    left = false;
  }

  function outclick(e) {
    if (!left) {
      deselect();
    }
    left = false;
  }
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<div class="px-4 text-xs h-full overflow-x-auto overflow-y-auto" style={pt}>
  <table
    id="summary-table"
    class="outline-none w-full"
    class:center
    bind:this={table}
    use:draggable
    use:clickOutside={[table]}
    tabindex="0"
    on:keydown={keydown}
    on:dragstart={dragstart}
    on:drag={drag}
    on:dragend={dragend}
    on:outclick={outclick}
    on:mouseleave={mouseleave}
    on:mouseenter={mouseenter}>
    {#each data.sections as section, typeIndex}
      {@const index = data.sections[typeIndex - 1] ? data.sections[typeIndex - 1].index : 0}
      {#if section.rows.length || report?.show_categories_with_no_items}
        {#key section.id}
          <ReportSection
            {group_by}
            {group}
            {section}
            {report}
            {columns}
            {selected}
            {typeIndex}
            {index}
            {linkPrefix}
            {attachments} />
        {/key}
      {/if}
    {/each}
    {#if report?.group_by !== "notes"}
      <tr>
        <td colspan={columns.length + 2} class="text-right">
          <div class="text-sm font-bold">
            {#if report?.group_by === "linear"}
              {#if du === "millimeters"}
                Total Length (mm): {(data.totals.total_length * 25.4).toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })}
              {:else}
                Total Length (in): {data.totals.total_length.toLocaleString("en-US", {
                  maximumFractionDigits: 2,
                })}
              {/if}
            {:else if report?.category_callout === "area"}
              Total Area (Block): {areaFormatted}
            {:else if report?.category_callout === "quantity" && group_by === "collection_id"}
              Total Qty of Openings: {data.totals.collection_count}
            {:else if report?.category_callout === "units"}
              Total Units: {data.totals.units}
            {/if}
          </div>
        </td>
      </tr>
    {/if}
  </table>
</div>

<style lang="scss">
  table {
    min-width: 36rem;
    &.center {
      margin: 0 auto;
    }
  }
</style>
